import React, { forwardRef, useState, useLayoutEffect } from 'react';
import {
  CodeToolbar,
  TextareaCode,
  textareaCodeInitialState,
  formatSelectOptions,
  SelectButtons,
} from '../';
import { useQueryParams } from '../../hooks';
import { technicalDefinitionLanguageLabel } from '../../helpers';

import './TechnicalDefinition.css';

/**
 * Shows an asset's technical definition, with the compiled/raw toggle where both exist.
 *
 * The two values are props rather than columns read off `data` by name, so the same component
 * serves a dataset's definition and a field's. They default to the dataset's columns, which is what
 * every caller passing only `data` relies on.
 *
 * @param {object} props
 * @param {object} props.data - the asset, and the source of the defaults below
 * @param {string} [props.definition] - the definition to show, defaulting to the dataset's
 * @param {string} [props.definitionRaw] - its raw form, defaulting to the dataset's. An asset with
 *   none simply has no toggle.
 */
const _TechnicalDefinition = (
  {
    data,
    definition = data?.dataset_technical_definition,
    definitionRaw = data?.dataset_technical_definition_raw,
    showLineNumbers = true,
    showInlineToolbar = false,
    showToolbar = true,
    language = 'sql',
  },
  ref
) => {
  const { queryParams, setHistory } = useQueryParams();

  const [technicalDefinitionViewMode, setTechnicalDefinitionViewMode] = useState(
    queryParams.technicalDefinitionViewMode
  );

  useLayoutEffect(() => {
    if (data && technicalDefinitionViewMode === language && !definition) {
      setTechnicalDefinitionViewMode('raw');
    } else if (data && technicalDefinitionViewMode === 'raw' && !definitionRaw) {
      setTechnicalDefinitionViewMode(language);
    }
  }, [data]);

  const toggleTechnicalDefinitionViewMode = event => {
    const value = event.value;
    setTechnicalDefinitionViewMode(value);
    setHistory({ technicalDefinitionViewMode: value });
  };

  if (!data) {
    return;
  }

  const getTechnicalDefinitionViewModes = () => {
    return formatSelectOptions({
      options: [
        {
          value: language,
          label: technicalDefinitionLanguageLabel(language),
          disabled: !definition,
          tooltip:
            `Compiled ${technicalDefinitionLanguageLabel(language)} definition` +
            (!definition ? ' not available' : ''),
        },
        {
          value: 'raw',
          label: 'Raw',
          disabled: !definitionRaw,
          tooltip: 'Raw Dbt code' + (!definitionRaw ? ' not available' : ''),
        },
      ],
      optionValue: 'value',
      optionLabel: 'label',
    });
  };

  const getTechnicalDefinition = () => {
    let technicalDefinition;
    switch (technicalDefinitionViewMode) {
      case language:
        technicalDefinition = definition;
        break;
      case 'raw':
        technicalDefinition = definitionRaw;
        break;
      default:
        technicalDefinition = definition;
    }
    return technicalDefinition && technicalDefinition.trim();
  };

  return (
    <div className="TechnicalDefinition" ref={ref}>
      {showToolbar && (
        <CodeToolbar language={language} copyText={getTechnicalDefinition()}>
          {/* Only where there is something to choose between; without a raw form the toolbar
              names the language itself. */}
          {definitionRaw && (
            <SelectButtons
              name="technical_definition_view"
              design="pill"
              value={technicalDefinitionViewMode}
              options={getTechnicalDefinitionViewModes()}
              onChange={toggleTechnicalDefinitionViewMode}
            />
          )}
        </CodeToolbar>
      )}

      <TextareaCode
        name="code-editor"
        value={textareaCodeInitialState({ value: getTechnicalDefinition() })}
        readOnly={true}
        language={language}
        showLineNumbers={showLineNumbers}
      />
    </div>
  );
};

export const TechnicalDefinition = React.memo(forwardRef(_TechnicalDefinition));
